import React, { useCallback, useState } from "react";
import { Card } from "react-bootstrap";
import { fetchCurrentUser, fetchWithAuth } from "../../../../api-client/APIClient";
import useAppContext from "../../../context/useAppContext";
import useProductRestrictions from "../../../shared/hooks/useProductRestrictions";
import { SectionHeaderWrapper } from "../../../shared/SectionHeaderWrapper";
import { showErrorToast, showSuccessToast } from "../../../shared/Toasts";
import { Toggle, Text } from "@merge-api/merge-javascript-shared";
import { DOCS_WEBHOOKS_THIRD_PARTIES_TO_MERGE_PATH } from "../../../../router/RouterUtils";

const ConfigurationWebhookReceiversPage = () => {
  const { user, setUser, isUserPrivileged } = useAppContext();
  const { productRestrictions } = useProductRestrictions();
  const areThirdPartyWebhooksEnabled = Boolean(productRestrictions?.third_party_webhooks_enabled);
  const [isEnabled, setIsEnabled] = useState<boolean>(user.organization.enable_auto_webhook_setup);

  const updateOrganizationEnableAutoWebhookSetup = useCallback(
    (newIsEnabled: boolean) => {
      fetchWithAuth({
        path: "/organizations/toggle-enable-auto-webhook-setup",
        method: "PATCH",
        body: {
          enable_auto_webhook_setup: newIsEnabled,
        },
        onResponse: (data) => {
          fetchCurrentUser(setUser);
          setIsEnabled(data.enable_auto_webhook_setup);
          showSuccessToast(
            `Successfully ${newIsEnabled ? "enabled" : "disabled"} automatic webhooks!`,
          );
        },
        onError: () => {
          showErrorToast("Something went wrong, please check your connection and try again.");
        },
      });
    },
    [setUser],
  );

  return (
    <SectionHeaderWrapper
      title="Third-party webhooks sent to Merge"
      docsUrl={DOCS_WEBHOOKS_THIRD_PARTIES_TO_MERGE_PATH}
    >
      <Card className="p-5 flex flex-row flex-wrap gap-y-5 items-center justify-between">
        <div className="flex flex-col gap-y-3">
          <Text variant="h4">Allow automatic webhook creation</Text>

          <Text>
            You can manage what events are receiving updates in each Linked Account's webhook page
          </Text>
        </div>
        <Toggle
          checked={isEnabled}
          disabled={!isUserPrivileged || !areThirdPartyWebhooksEnabled}
          label={isEnabled ? "Enabled" : "Disabled"}
          onChange={updateOrganizationEnableAutoWebhookSetup}
          toggleTooltipTitle={
            !isUserPrivileged
              ? "Must be an admin user to change this setting"
              : !areThirdPartyWebhooksEnabled
              ? "Upgrade your plan to enable third party webhooks in order to unlock this feature"
              : ""
          }
        />
      </Card>
    </SectionHeaderWrapper>
  );
};

export default ConfigurationWebhookReceiversPage;
